import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Radio, OptionTypeWithCode, Select, Typography } from '@beeline.com/bp-design-system';
import { PageModelInput, ValueModelInput } from '@utmost.co/enterprise-models';
import { CustomFieldDefinitionModelInput } from 'src/graphql/types';
import { useTypedQuery } from 'src/utils/hooks/useTypedQuery';
import { CustomFieldFormDataTest } from '../utils/CustomFieldsDefinitions.constants';
import { useCustomFieldsAndObjectsIntl } from '../../translations/useCustomFieldsAndObjectsIntl';
import { GetCustomFieldCostCenterValuesDocument } from '../utils/__generated__/GetCustomFieldCostCenterValues.graphql';
import { AceEditor } from 'src/components/common/AceEditor/AceEditor';

export interface SaveCustomFieldFormProps {
  onCustomFieldChange: (customFieldDefinition: CustomFieldDefinitionModelInput) => void;
  customFieldDefinition: CustomFieldDefinitionModelInput | undefined;
}

const defaultPage: PageModelInput = { limit: 25, offset: 0 };

export const CustomFieldCostCenterValuesForm: FC<React.PropsWithChildren<SaveCustomFieldFormProps>> = ({
  onCustomFieldChange,
  customFieldDefinition
}) => {
  const intl = useCustomFieldsAndObjectsIntl();
  const [singleMode, setSingleMode] = useState(false);
  const { loading, get, data } = useTypedQuery(GetCustomFieldCostCenterValuesDocument);

  useEffect(() => {
    get({ variables: { query: { assignable: true }, page: defaultPage } });
  }, [get]);

  const values: string[] = useMemo(
    () => customFieldDefinition?.values?.map((value) => value?.referenceId ?? '') ?? [],
    [customFieldDefinition]
  );
  const selectedValues: string[] = useMemo(() => values.filter(Boolean), [values]);

  const OPTIONS: OptionTypeWithCode<string>[] = useMemo(
    () =>
      data?.costCenters?.items
        ?.filter((option) => !!option?.referenceId)
        .map((_) => ({
          label: `${_!.name}`,
          value: _!.referenceId || '',
          code: _!.referenceId || ''
        })) || [],
    [data]
  );
  const hasMoreResults = (data?.costCenters?.totalCount ?? 0) > OPTIONS.length;

  const handleChange = useCallback(
    (e: string) => {
      const uids = e.split('\n');
      // Remove duplicates
      const uniqueUids = Array.from(new Set(uids));
      const newValues = uniqueUids.map(
        (value) =>
          ({
            referenceId: value
          }) as ValueModelInput
      );
      onCustomFieldChange({
        ...customFieldDefinition,
        values: newValues
      });
    },
    [customFieldDefinition, onCustomFieldChange]
  );

  const handleAddCostCenter = useCallback(
    (e: string[]) => {
      onCustomFieldChange({
        ...customFieldDefinition,
        values: e.map((value) => ({ referenceId: value }) as ValueModelInput)
      });
    },
    [customFieldDefinition, onCustomFieldChange]
  );

  return (
    <StyledForm data-test={CustomFieldFormDataTest.CostCenters}>
      <Typography variant="label1">{intl.introText}</Typography>
      <StyledRadioSection>
        <div>
          <Radio
            checked={singleMode}
            onClick={() => setSingleMode(true)}
            dataTest={CustomFieldFormDataTest.SingleMode}
          />
        </div>
        <div>
          <Typography variant="label1">{intl.singleMode}</Typography>
          <Typography variant="body2">{intl.singleModeExplainer}</Typography>
        </div>
      </StyledRadioSection>
      {singleMode && (
        <StyledSingleSelector>
          <Typography variant="label1">{intl.costCenterLabel}</Typography>
          <Select<string, true>
            dataTest={CustomFieldFormDataTest.CostCentersFilter}
            placeholder={intl.addCostCenter}
            loading={loading}
            autoComplete
            fullWidth
            multiple
            value={selectedValues}
            options={OPTIONS}
            onSearch={(term) => get({ variables: { query: { search: term, assignable: true }, page: defaultPage } })}
            onChange={handleAddCostCenter}
            menuProps={{ showLoadMore: hasMoreResults }}
          />
        </StyledSingleSelector>
      )}
      <StyledRadioSection>
        <div>
          <Radio
            checked={!singleMode}
            onClick={() => setSingleMode(false)}
            dataTest={CustomFieldFormDataTest.TextMode}
          />
        </div>
        <div>
          <Typography variant="label1">{intl.textMode}</Typography>
          <Typography variant="body2">{intl.textModeExplainer}</Typography>
        </div>
      </StyledRadioSection>
      {!singleMode && (
        <AceEditor
          disabled={loading}
          label={intl.valuesLabel}
          dataTest={CustomFieldFormDataTest.CostCentersList}
          value={values.join('\n')}
          helperText={intl.instanceValuesHelperText}
          onChange={handleChange}
          enableLiveAutocompletion={false}
          enableBasicAutocompletion={false}
          mode="text"
          minLines={5}
          maxLines={10}
        />
      )}
    </StyledForm>
  );
};

const StyledRadioSection = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacingSize.m};
  align-items: center;
`;

const StyledSingleSelector = styled.div`
  > .label1 {
    margin: 0 0 0.5rem 0;
  }
`;

const StyledForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacingSize.l};
`;
